import React from 'react'
import classNames from 'classnames'
import { usePageContext } from '@toasttab/shared/src/hooks/usePageContext'
import { Button } from '@toasttab/shared/src/components'
import TextLink from '@toasttab/shared/src/components/molecules/TextLink/TextLink'
import { useI18next } from '@toasttab/shared/src/hooks/useI18next'

interface LinkConfig {
  placeholder: string
  href: string
  text: string
}

interface LoginConfig {
  enabled?: boolean
  text?: string
  linkText?: string
  linkHref?: string
  buttonText?: string
  buttonHref?: string
  separatorText?: string
}

interface ConsentConfig {
  text: string
  links: LinkConfig[]
}

interface PrivacyStatementData {
  login?: LoginConfig
  consent?: ConsentConfig
}

interface PrivacyStatementProps {
  center?: boolean
  data?: PrivacyStatementData
}

const PrivacyStatement = ({ center = false, data }: PrivacyStatementProps) => {
  const { t } = useI18next()
  const pageContext = usePageContext()
  const { styleContext } = pageContext
  const loginBlockClasses = classNames(
    'mb-4 text-base font-normal leading-normal font-sansPro tracking-normal text-secondary mt-4',
    {
      'text-center': center
    }
  )

  const renderLoginBlock = () => {
    // If data is provided and login is explicitly disabled, return null
    if (data?.login?.enabled === false) {
      return null
    }

    // If custom login data is provided, use it
    if (data?.login) {
      const login = data.login

      if (styleContext === 'hpVariant') {
        return (
          <>
            <div className='flex items-center mb-4'>
              <hr className='flex-grow border-t border-black/80' />
              <span className='mx-4 text-gray-500'>
                {login.separatorText || t('or')}
              </span>
              <hr className='flex-grow border-t border-black/80' />
            </div>
            <Button
              fontWeight={['font-semibold']}
              fullwidth
              heapInteraction='hprefine_bottomdemoLogin_submit'
              href={login.buttonHref || 'https://www.toasttab.com/login'}
              size='medium'
              variation='secondary'
            >
              {login.buttonText || t('Already Use Toast? Log In')}
            </Button>
          </>
        )
      } else {
        return (
          <>
            {login.text || t('Already use Toast at your restaurant?')}{' '}
            <TextLink
              classes='block md:inline'
              color='default'
              fontWeight={['font-normal']}
              href={login.linkHref || 'https://www.toasttab.com/login'}
              text={login.linkText || t('Log in to Toast')}
              underline
            />
          </>
        )
      }
    }

    // Default behavior (current implementation)
    if (styleContext === 'hpVariant') {
      return (
        <>
          <div className='flex items-center mb-4'>
            <hr className='flex-grow border-t border-black/80' />
            <span className='mx-4 text-gray-500'>{t('or')}</span>
            <hr className='flex-grow border-t border-black/80' />
          </div>
          <Button
            fontWeight={['font-semibold']}
            fullwidth
            heapInteraction='hprefine_bottomdemoLogin_submit'
            href='https://www.toasttab.com/login'
            size='medium'
            variation='secondary'
          >
            {t('Already Use Toast? Log In')}
          </Button>
        </>
      )
    } else {
      return (
        <>
          {t('Already use Toast at your restaurant?')}{' '}
          <TextLink
            classes='block md:inline'
            color='default'
            fontWeight={['font-normal']}
            href='https://www.toasttab.com/login'
            text={t('Log in to Toast')}
            underline
          />
        </>
      )
    }
  }

  const consentLinks = (
    text: string,
    links?: LinkConfig[]
  ): React.ReactNode[] => {
    // If custom links are provided, use them
    if (links && links.length > 0) {
      const placeholderRegex = new RegExp(
        `(${links
          .map((link) =>
            link.placeholder.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
          )
          .join('|')})`,
        'g'
      )

      return text.split(placeholderRegex).map((chunk, index) => {
        const linkConfig = links.find((link) => link.placeholder === chunk)
        if (linkConfig) {
          return (
            <a
              className='underline text-link inline-block'
              href={linkConfig.href}
              key={`link-${index}`}
            >
              {linkConfig.text}
            </a>
          )
        }
        return chunk
      })
    }

    // Default behavior (current implementation)
    return text.split(/({{privacyLink}}|{{californiaLink}})/).map((chunk) => {
      if (chunk === '{{privacyLink}}') {
        return (
          <a
            className='underline text-link inline-block'
            href='/privacy/'
            key='privacy'
          >
            {t('privacy statement')}
          </a>
        )
      }
      if (chunk === '{{californiaLink}}') {
        return (
          <a
            className='underline text-link inline-block'
            href='/privacy#addendum-a'
            key='ca'
          >
            {t('here')}
          </a>
        )
      }
      return chunk
    })
  }

  const getConsentText = () => {
    if (data?.consent) {
      return data.consent.text
    }
    return t('demo_consent', {
      defaultValue:
        "By requesting a demo, you agree to receive automated text messages from Toast. We'll handle your info according to our {{privacyLink}}. Additional information for California residents available {{californiaLink}}."
    })
  }

  const loginBlock = renderLoginBlock()

  return (
    <>
      {loginBlock ? (
        <div className={loginBlockClasses}>{loginBlock}</div>
      ) : null}
      <p
        className={`text-xs font-normal leading-4 font-sansPro text-secondary ${
          center ? 'text-center' : ''
        }`}
      >
        {consentLinks(getConsentText(), data?.consent?.links)}
      </p>
    </>
  )
}

export default PrivacyStatement
